import { useEffect, useState } from 'react';

import {
  Accordion,
  Badge,
  Card,
  IconButton,
  LineChart,
  ListSkeleton,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@opositandopn/ui';
import {
  IoArrowForwardCircleOutline,
  IoClipboardOutline,
} from 'react-icons/io5';
import { useWindowSize } from 'usehooks-ts';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { useIsPremium } from '../../../../context/ProfileContext';
import { useAppActions } from '../../../../state/appStore';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { dateDiffInDays, formatSortDayDate } from '../../../../utils/date';
import { useGetSubject } from '../../../subjects/api/useSubjects';
import { colorPalette } from '../../../subjects/components/subject-selectable-list/SubjectSelectableList';
import { useGetMultipleChoiceAssessments } from '../../api/useMultipleChoiceAssessments';
import { AssessmentListFilterForm } from '../assessment-list-filter/AssessmentListFilter';
import { MultipleChoiceAssessmentPreview } from '../multiple-choice-assessment-preview/MultipleChoiceAssessmentPreview';

import styles from './MultipleChoiceAssessmentGroupedList.module.scss';

const DAYS_TO_SHOW_BADGE = 5;

type ContainerProps = BaseComponentProps & {
  filter: AssessmentListFilterForm;
  onSelected?: (assessmentId: string) => void;
};

function MultipleChoiceAssessmentGroupedList({
  className,
  filter,
  onSelected,
  testId = 'ui-multiple-choice-assessment-grouped-list',
}: ContainerProps) {
  const isPremium = useIsPremium();
  const { setShowGetPremiumPopup } = useAppActions();
  const { width } = useWindowSize();

  const date = new Date();
  const [current, setCurrent] = useState<string>();
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
  });

  const { data: subjectResponse, isLoading: isSubjectLoading } = useGetSubject({
    subjectId: filter.subject.id,
  });

  const { data: response, isLoading } = useGetMultipleChoiceAssessments({
    subjectId: filter.subject.id,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    fromTime: filter.fromTime,
    toTime: filter.toTime,
    sort: filter.sort,
    params: {
      text: filter.text,
      statuses: filter.statuses,
      visibilities: filter.visibilities,
      purposes: filter.purposes,
      referenceStandards: filter.referenceStandards,
      methodologies: filter.methodologies,
      perspectives: filter.perspectives,
      settings: filter.settings,
      difficultyLevels: filter.difficultyLevels,
      tagIds: filter.tags?.map((tag) => tag.id),
      questionIds: filter.questionIds,
      numberOfChoices: filter.numberOfChoices,
      official: filter.official,
      released: filter.released,
    },
  });

  useEffect(() => {
    setPagination({ ...pagination, pageNumber: 1 });
  }, [filter]);

  return (
    <>
      <Card title="Simulacros" className={className} testId={testId}>
        {isLoading || isSubjectLoading || !response ? (
          <ListSkeleton items={25} />
        ) : (
          <>
            {subjectResponse?.units.map((unit, index) => (
              <div key={unit.id}>
                {response.items.some(
                  (item) =>
                    item.unit?.id === unit.id && item.lesson !== undefined,
                ) && (
                  <>
                    <div className={styles['title']}>{unit.name}</div>
                    <Accordion
                      items={unit.lessons
                        .filter((lesson) =>
                          response.items.some(
                            (item) => item.lesson?.id === lesson.id,
                          ),
                        )
                        .map((lesson) => ({
                          label: {
                            value: (
                              '0' +
                              (subjectResponse.units
                                .filter((unit) => unit.index <= index)
                                .flatMap((unit) => unit.lessons).length +
                                lesson.index)
                            ).slice(-2),
                            backgroundColor:
                              colorPalette[index % colorPalette.length],
                          },
                          text: lesson.name,
                          tag: response.items.some(
                            (assessment) =>
                              assessment.lesson?.id === lesson.id &&
                              dateDiffInDays(
                                new Date(assessment.releaseDate),
                                date,
                              ) < DAYS_TO_SHOW_BADGE,
                          ) && <Badge bg="success">Nuevos</Badge>,
                          items: response.items
                            .filter((item) => item.lesson?.id === lesson.id)
                            .map((item) => ({
                              text:
                                item.name.length > 120
                                  ? item.name.substring(0, 120) + '…'
                                  : item.name,
                              info: item.name,
                              tag: (
                                <>
                                  {item.releaseDate &&
                                    dateDiffInDays(
                                      new Date(item.releaseDate),
                                      date,
                                    ) < DAYS_TO_SHOW_BADGE && (
                                      <Badge bg="success">Nuevo</Badge>
                                    )}
                                </>
                              ),
                              icon: (
                                <>
                                  <Text
                                    variety="annotation"
                                    font="monospace"
                                    style={{ marginRight: '5px' }}
                                  >
                                    {width > 576
                                      ? `${item.questions.length} preguntas`
                                      : `${item.questions.length} preg.`}
                                  </Text>
                                  {item.attemptScores &&
                                    item.attemptScores.length > 0 && (
                                      <Tooltip placement="left">
                                        <TooltipTrigger asChild={true}>
                                          <Badge bg="featured">
                                            {
                                              item.attemptScores[
                                                item.attemptScores.length - 1
                                              ].score
                                            }
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          className={styles['content']}
                                        >
                                          <LineChart
                                            labels={item.attemptScores.map(
                                              (score) =>
                                                formatSortDayDate(
                                                  new Date(score.date),
                                                ),
                                            )}
                                            dataset={[
                                              {
                                                label: 'Nota',
                                                values: item.attemptScores.map(
                                                  (score) => score.score,
                                                ),
                                              },
                                            ]}
                                          />
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  <IconButton
                                    style={{
                                      opacity:
                                        !isPremium &&
                                        item.visibility === 'private'
                                          ? 0.3
                                          : 1,
                                    }}
                                    size="sm"
                                    icon={<IoClipboardOutline />}
                                    onClick={() => {
                                      if (
                                        !isPremium &&
                                        item.visibility === 'private'
                                      ) {
                                        setShowGetPremiumPopup(true);
                                        return;
                                      }

                                      setCurrent(item.id);
                                    }}
                                  />
                                </>
                              ),
                            })),
                        }))}
                    />
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </Card>
      <ResourceDialog open={current !== undefined}>
        {current && (
          <MultipleChoiceAssessmentPreview
            subjectId={filter.subject.id}
            assessmentId={current}
            onClose={() => {
              setCurrent(undefined);
            }}
          >
            {onSelected && (
              <IconButton
                icon={<IoArrowForwardCircleOutline />}
                onClick={() => {
                  onSelected(current);
                }}
              />
            )}
          </MultipleChoiceAssessmentPreview>
        )}
      </ResourceDialog>
    </>
  );
}

export { MultipleChoiceAssessmentGroupedList };
